import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function ESGConsulting() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'ESG کنسلٹنگ - Galla Mandi Bills & Khata' : 'ESG Consulting - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-header via-green-700 to-brand-header text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              {isUrdu ? 'ESG کنسلٹنگ' : 'ESG Consulting'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium">
              {isUrdu 
                ? 'ماحولیاتی، سماجی اور حکمرانی کے معیارات کے لیے مشاورت' 
                : 'Consulting for Environmental, Social, and Governance standards'}
            </p>
          </div>
        </section>

        {/* ESG Services Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {isUrdu ? 'ہماری ESG خدمات' : 'Our ESG Services'}
              </h2>
              <p className="text-gray-600 text-lg">
                {isUrdu ? 'پائیدار کاروبار کے لیے مکمل حل' : 'Complete solutions for sustainable business'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "🌱",
                  title: isUrdu ? "ماحولیات" : "Environment",
                  titleUrdu: "ماحولیات",
                  desc: isUrdu ? "ماحولیاتی تحفظ کے اقدامات" : "Environmental protection measures",
                  descUrdu: "ماحولیاتی تحفظ کے اقدامات"
                },
                {
                  icon: "👥",
                  title: isUrdu ? "سماجی ذمہ داری" : "Social Responsibility",
                  titleUrdu: "سماجی ذمہ داری",
                  desc: isUrdu ? "معاشرے کے لیے مثبت اثرات" : "Positive impact on society",
                  descUrdu: "معاشرے کے لیے مثبت اثرات"
                },
                {
                  icon: "⚖️",
                  title: isUrdu ? "حکمرانی" : "Governance",
                  titleUrdu: "حکمرانی",
                  desc: isUrdu ? "شفاف اور اخلاقی حکمرانی" : "Transparent and ethical governance",
                  descUrdu: "شفاف اور اخلاقی حکمرانی"
                },
                {
                  icon: "📋",
                  title: isUrdu ? "رپورٹنگ" : "Reporting",
                  titleUrdu: "رپورٹنگ",
                  desc: isUrdu ? "ESG کارکردگی کی رپورٹنگ" : "ESG performance reporting",
                  descUrdu: "ESG کارکردگی کی رپورٹنگ"
                },
                {
                  icon: "🎯",
                  title: isUrdu ? "ہدف طے کرنا" : "Goal Setting",
                  titleUrdu: "ہدف طے کرنا",
                  desc: isUrdu ? "پائیداری کے اہداف" : "Sustainability goals",
                  descUrdu: "پائیداری کے اہداف"
                },
                {
                  icon: "✅",
                  title: isUrdu ? "تصدیق" : "Certification",
                  titleUrdu: "تصدیق",
                  desc: isUrdu ? "ESG معیارات کی تصدیق" : "ESG standards certification",
                  descUrdu: "ESG معیارات کی تصدیق"
                }
              ].map((service, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? service.titleUrdu : service.title}
                  </h3>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? service.descUrdu : service.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-green-700 text-white">
          <div className={`container mx-auto px-4 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-4">
              {isUrdu ? 'پائیداری شروع کریں' : 'Start Your Sustainability Journey'}
            </h2>
            <p className="text-xl mb-8 text-white/90">
              {isUrdu ? 'ESG مشاورت کے لیے آج ہی رابطہ کریں' : 'Contact us today for ESG consulting'}
            </p>
            <Link
              to="/contact"
              className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg inline-block"
            >
              {isUrdu ? 'رابطہ کریں' : 'Contact Us'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default ESGConsulting;

